import React, { useState, useRef } from 'react';
import { toast } from 'react-toastify';
import { getAuthHeaders } from '../../auth';
import { openFilePreview } from '../common/FilePreviewModal';

const API_BASE = '/api/common/missing-documents/';

const SOURCE_OPTIONS = [
    { id: 'job_documents', text: 'Job Documents' },
    { id: 'other_payables', text: 'Other Payables' },
    { id: 'shipping_line_payables', text: 'Shipping Line Payables' },
    { id: 'shipping_do_documents', text: 'Shipping DO Documents' },
];

const MissingDocuments = () => {
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [source, setSource] = useState('job_documents');
    const [missing, setMissing] = useState([]);
    const [loading, setLoading] = useState(false);
    const [savingId, setSavingId] = useState(null);
    const [selectedFiles, setSelectedFiles] = useState({});
    const [expandedJobs, setExpandedJobs] = useState(new Set());
    const [sortConfig, setSortConfig] = useState({ key: 'job_id', direction: 'desc' });
    const fileRefs = useRef({});

    const handleSearch = async (e) => {
        e.preventDefault();
        if (!startDate || !endDate) {
            toast.error('Please provide a start and end date.');
            return;
        }
        setLoading(true);
        try {
            const url = `${API_BASE}?source=${encodeURIComponent(source)}&start_date=${encodeURIComponent(startDate)}&end_date=${encodeURIComponent(endDate)}`;
            const res = await fetch(url, { headers: getAuthHeaders() });
            if (!res.ok) throw new Error((await res.json()).error || 'Search failed');
            const data = await res.json();
            setMissing(data.missing || []);
            setSelectedFiles({});
        } catch (err) {
            toast.error(err.message || 'Failed to load missing documents.');
        } finally {
            setLoading(false);
        }
    };

    const handleFileSelect = (id, file) => {
        setSelectedFiles(prev => ({ ...prev, [id]: file }));
    };

    const handleSave = async (id) => {
        const file = selectedFiles[id];
        if (!file) {
            toast.error('Please select a replacement file first.');
            return;
        }
        setSavingId(id);
        try {
            const formData = new FormData();
            formData.append('document_file', file);
            const res = await fetch(`${API_BASE}${id}/replace/`, {
                method: 'POST',
                headers: getAuthHeaders(true),
                body: formData,
            });
            if (!res.ok) throw new Error((await res.json()).error || 'Save failed');
            toast.success('Document file saved successfully.');
            setMissing(prev => prev.filter(item => item.id !== id));
            setSelectedFiles(prev => { const next = { ...prev }; delete next[id]; return next; });
        } catch (err) {
            toast.error(err.message || 'Failed to save document file.');
        } finally {
            setSavingId(null);
        }
    };

    const handleView = (url, name = 'Document') => {
        if (url) openFilePreview(url, name);
    };

    const handleViewSelected = (file) => {
        if (!file) return;
        const url = URL.createObjectURL(file);
        openFilePreview(url, file.name);
    };

    const triggerReplace = (id) => {
        fileRefs.current[id]?.click();
    };

    const toggleJob = (jobId) => {
        setExpandedJobs(prev => {
            const next = new Set(prev);
            if (next.has(jobId)) {
                next.delete(jobId);
            } else {
                next.add(jobId);
            }
            return next;
        });
    };

    const requestSort = (key) => {
        setSortConfig(prev => {
            if (prev.key === key) {
                return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
            }
            return { key, direction: 'asc' };
        });
    };

    const groupedMissing = React.useMemo(() => {
        const groups = {};
        missing.forEach(item => {
            if (!groups[item.job_id]) {
                groups[item.job_id] = { job_id: item.job_id, idf_no: item.idf_no, customer_short_name: item.customer_short_name, docs: [] };
            }
            groups[item.job_id].docs.push(item);
        });
        return groups;
    }, [missing]);

    const sortedGroups = React.useMemo(() => {
        const groups = Object.values(groupedMissing);
        if (!sortConfig) return groups;
        return [...groups].sort((a, b) => {
            if (sortConfig.key === 'job_id') {
                return sortConfig.direction === 'asc' ? a.job_id - b.job_id : b.job_id - a.job_id;
            }
            if (sortConfig.key === 'idf_no') {
                const aVal = String(a.idf_no || '').toLowerCase();
                const bVal = String(b.idf_no || '').toLowerCase();
                return sortConfig.direction === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
            }
            return 0;
        });
    }, [groupedMissing, sortConfig]);

    return (
        <div className="missing-documents-container" style={{ padding: 20 }}>
            <style>{`
                .missing-row:hover td { background-color: #e6f7ff !important; }
                .missing-detail:hover td { background-color: #f0f8ff !important; }
                .doc-row { padding: 4px 6px; border-radius: 4px; }
                .doc-row:hover { background-color: #f0f8ff; }
            `}</style>
            <h2>Missing Documents Tool</h2>
            <form onSubmit={handleSearch} style={{ display: 'flex', gap: 15, alignItems: 'flex-end', marginBottom: 20, flexWrap: 'wrap' }}>
                <div>
                    <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>Source</label>
                    <select
                        value={source}
                        onChange={(e) => setSource(e.target.value)}
                        style={{ padding: '6px 10px', minWidth: 200 }}
                    >
                        {SOURCE_OPTIONS.map(opt => (
                            <option key={opt.id} value={opt.id}>{opt.text}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>Start Date</label>
                    <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} style={{ padding: 6 }} />
                </div>
                <div>
                    <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>End Date</label>
                    <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} style={{ padding: 6 }} />
                </div>
                <button type="submit" disabled={loading} style={{ padding: '7px 16px' }}>
                    {loading ? 'Searching...' : 'Search'}
                </button>
            </form>

            {missing.length > 0 && (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                        <tr style={{ background: '#f0f0f0', textAlign: 'left' }}>
                            <th
                                onClick={() => requestSort('job_id')}
                                style={{ padding: 8, border: '1px solid #ccc', cursor: 'pointer', userSelect: 'none' }}
                            >
                                Job ID {sortConfig.key === 'job_id' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                            </th>
                            <th
                                onClick={() => requestSort('idf_no')}
                                style={{ padding: 8, border: '1px solid #ccc', cursor: 'pointer', userSelect: 'none' }}
                            >
                                IDF No {sortConfig.key === 'idf_no' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                            </th>
                            <th style={{ padding: 8, border: '1px solid #ccc' }}>Customer</th>
                            <th style={{ padding: 8, border: '1px solid #ccc' }}>Details</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedGroups.map(group => (
                            <React.Fragment key={group.job_id}>
                                <tr className="missing-row">
                                    <td style={{ padding: 8, border: '1px solid #ccc' }}>{group.job_id}</td>
                                    <td style={{ padding: 8, border: '1px solid #ccc' }}>{group.idf_no || '-'}</td>
                                    <td style={{ padding: 8, border: '1px solid #ccc', fontWeight: 'bold' }}>{group.customer_short_name || '-'}</td>
                                    <td style={{ padding: 8, border: '1px solid #ccc' }}>
                                        <button
                                            type="button"
                                            onClick={() => toggleJob(group.job_id)}
                                            style={{ padding: '4px 10px' }}
                                        >
                                            {expandedJobs.has(group.job_id) ? 'Hide Details' : 'Details'}
                                        </button>
                                    </td>
                                </tr>
                                {expandedJobs.has(group.job_id) && (
                                    <tr className="missing-detail">
                                        <td colSpan={4} style={{ padding: 10, border: '1px solid #ccc', background: '#fafafa' }}>
                                            {group.docs.map(doc => (
                                                <div
                                                    key={doc.id}
                                                    className="doc-row"
                                                    style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 6 }}
                                                >
                                                    {(source === 'other_payables' || source === 'shipping_line_payables') && (
                                                        <span style={{ minWidth: 120, fontWeight: 500 }}>{doc.payable_id || '-'}</span>
                                                    )}
                                                    {source === 'shipping_do_documents' && (
                                                        <span style={{ minWidth: 200, fontWeight: 500 }}>{doc.record_info || '-'}</span>
                                                    )}
                                                    <span style={{ minWidth: 180, fontWeight: 500 }}>{doc.document_type || '-'}</span>
                                                    <span style={{ minWidth: 200, color: '#555' }}>{doc.document_name || '-'}</span>
                                                    <button type="button" onClick={() => handleView(doc.file_url, doc.document_type || 'Document')} style={{ padding: '4px 10px' }}>
                                                        View
                                                    </button>
                                                    <button type="button" onClick={() => triggerReplace(doc.id)} style={{ padding: '4px 10px' }}>
                                                        Replace
                                                    </button>
                                                    <input
                                                        type="file"
                                                        ref={(el) => { if (el) fileRefs.current[doc.id] = el; }}
                                                        onChange={(e) => handleFileSelect(doc.id, e.target.files[0])}
                                                        style={{ display: 'none' }}
                                                    />
                                                    {selectedFiles[doc.id] && (
                                                        <>
                                                            <span style={{ fontSize: 11, color: '#007bff' }}>
                                                                {selectedFiles[doc.id].name}
                                                            </span>
                                                            <button type="button" onClick={() => handleViewSelected(selectedFiles[doc.id])} style={{ padding: '4px 10px' }}>
                                                                View
                                                            </button>
                                                        </>
                                                    )}
                                                    <button
                                                        type="button"
                                                        onClick={() => handleSave(doc.id)}
                                                        disabled={savingId === doc.id || !selectedFiles[doc.id]}
                                                        style={{ padding: '4px 10px' }}
                                                    >
                                                        {savingId === doc.id ? 'Saving...' : 'Save'}
                                                    </button>
                                                </div>
                                            ))}
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            )}

            {!loading && missing.length === 0 && (
                <p style={{ color: '#666', marginTop: 20 }}>No missing documents found for the selected criteria.</p>
            )}
        </div>
    );
};

export default MissingDocuments;
